import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { Trans } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { appRouterPaths } from "@/services/navigation";
import type { TabImproved } from "@/shared/Components";
import type { MaybeType } from "@/shared/types";
import { contactsTabsActions, contactsTabsSelectors } from "../slice";
import { SPECIAL_TABS } from "../constants";

export const useContactsTabs = (selectedPersonId: MaybeType<string>) => {
  const dispatch = useAppDispatch();

  const activeTab = useAppSelector(contactsTabsSelectors.selectActiveTab);
  const isEditMode = useAppSelector(contactsTabsSelectors.selectIsEditMode);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const isEditModeFromParams = searchParams.get("isEditMode");

  const handleTabChange = useCallback(
    (key: string) => {
      dispatch(contactsTabsActions.setActiveTab(key));

      if (SPECIAL_TABS.includes(key)) {
        dispatch(contactsTabsActions.setIsEditMode(false));
      }

      navigate(`${key.replace(":personId", selectedPersonId ?? "")}`);
    },
    [selectedPersonId, navigate, dispatch],
  );

  const handleEditMode = useCallback(() => {
    dispatch(contactsTabsActions.setIsEditMode(!isEditMode));
  }, [isEditMode]);

  useEffect(() => {
    dispatch(contactsTabsActions.setActiveTab(appRouterPaths.contacts.genInfo));
    dispatch(contactsTabsActions.setIsEditMode(false));

    if (selectedPersonId === "TEMP_ID" || isEditModeFromParams) {
      dispatch(contactsTabsActions.setIsEditMode(true));
    }
  }, [selectedPersonId, isEditModeFromParams]);

  const disabledKeys = useMemo(() => {
    return selectedPersonId === "TEMP_ID" ? SPECIAL_TABS : [];
  }, [selectedPersonId]);

  const tabs: TabImproved[] = useMemo(() => {
    return [
      {
        key: appRouterPaths.contacts.genInfo,
        label: <Trans i18nKey={"contactsPage.tabs.general"} />,
        children: <Outlet context={{ isEditMode, handleEditMode }} />,
      },
      {
        key: appRouterPaths.contacts.activities,
        label: <Trans i18nKey={"contactsPage.tabs.activities"} />,
        children: <Outlet context={{ isEditMode, handleEditMode }} />,
      },
      {
        key: appRouterPaths.contacts.connections,
        label: <Trans i18nKey={"contactsPage.tabs.connections"} />,
        children: <Outlet />,
      },
      {
        key: appRouterPaths.contacts.commitments,
        label: <Trans i18nKey={"contactsPage.tabs.commitments"} />,
        children: <Outlet />,
      },
      {
        key: appRouterPaths.contacts.notes,
        label: <Trans i18nKey={"contactsPage.tabs.notes"} />,
        children: <Outlet />,
      },
      {
        key: appRouterPaths.contacts.ideas,
        label: <Trans i18nKey={"contactsPage.tabs.ideas"} />,
        children: <Outlet />,
      },
    ];
  }, [isEditMode, handleEditMode]);

  return {
    state: {
      activeTab,
      isEditMode,
      disabledKeys,
      tabs,
    },
    methods: {
      handleTabChange,
      handleEditMode,
    },
  };
};
